import { useMemo } from 'react'
import {
  useAuthStore,
  useVerticalConfig,
  usePermission,
  useTenantWidePermission,
  useResolvedPermissions,
  useVerticalModule,
  type VerticalNavItem,
} from '@lipaflow/shared'
import {
  HomeIcon,
  UsersIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  HandThumbUpIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  MegaphoneIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  ShieldExclamationIcon,
  ChartBarSquareIcon,
  EyeIcon,
  LightBulbIcon,
  BanknotesIcon,
  ReceiptPercentIcon,
  InboxArrowDownIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline'
import { COLLECTIONS } from './features'
import { useSettingsTabs } from './settingsTabs'

export type NavChild = {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  keywords?: string[]
  /** Pages shown as tabs under this one entry; `href` is the first of them. */
  tabs?: NavChild[]
}

export type NavItem = {
  name: string
  href?: string
  icon?: React.ComponentType<{ className?: string }>
  children?: NavChild[]
  keywords?: string[]
  tabs?: NavChild[]
}

/**
 * One menu entry for several pages of one job (Payments: approvals, payments
 * received, deposits to assign, opening balances), shown as tabs on the page.
 * One page is just that page; none is nothing.
 */
function hub(
  name: string,
  icon: React.ComponentType<{ className?: string }>,
  pages: NavChild[],
): NavChild | null {
  if (pages.length === 0) return null
  if (pages.length === 1) return pages[0]
  return {
    name,
    href: pages[0].href,
    icon,
    keywords: pages.flatMap((page) => [page.name.toLowerCase(), ...(page.keywords ?? [])]),
    tabs: pages,
  }
}

/**
 * The only page someone can open besides Home, if there is just one (a
 * caretaker's Repairs). They land there, and Home leaves their menu.
 */
export function soleDestination(navigation: NavItem[]): string | null {
  const places = navigation.flatMap((item) =>
    item.children ? item.children.map((child) => child.href) : item.href && item.href !== '/' ? [item.href] : [],
  )
  return places.length === 1 ? places[0] : null
}

/** The hub (with its tabs) that a path belongs to, if any. */
function withKeyword(entry: NavChild | null, keyword: string): NavChild | null {
  return entry && { ...entry, keywords: [...(entry.keywords ?? []), keyword] }
}

export function hubForPath(navigation: NavItem[], pathname: string): NavChild | null {
  const entries: (NavItem | NavChild)[] = navigation.flatMap((item) => [item, ...(item.children ?? [])])
  for (const entry of entries) {
    if (entry.tabs?.some((tab) => pathname === tab.href || pathname.startsWith(`${tab.href}/`))) {
      return entry as NavChild
    }
  }
  return null
}

function pluralize(s: string) {
  if (s.endsWith('y')) return s.slice(0, -1) + 'ies'
  if (s.endsWith('s') || s.endsWith('x') || s.endsWith('ch')) return s + 'es'
  return s + 's'
}

export function useNavigation() {
  const { labels } = useVerticalConfig()
  // The vertical's own pages arrive through its module, not through feature
  // flags this file would have to know the meaning of (ADR-0004 §3).
  const verticalModule = useVerticalModule()
  const operatingModel = useAuthStore((state) => state.tenant?.operating_model ?? null)
  const permissions = useResolvedPermissions()
  const features = useVerticalConfig().features
  const canViewAudit = usePermission('audit:view')
  const canViewSettings = usePermission('settings:view')
  const settingsTabs = useSettingsTabs()
  const canViewBeneficiaries = usePermission('beneficiaries:view')
  const canViewPayments = usePermission('payments:view')
  const canViewTenantPayments = useTenantWidePermission('payments:view')
  const canViewPlans = usePermission('plans:view')
  const canViewPledges = usePermission('pledges:view')
  const canRequestWaivers = usePermission('waivers:request')
  const canApproveWaivers = usePermission('waivers:approve')
  const canSeeWaivers = canRequestWaivers || canApproveWaivers
  const canViewReports = usePermission('reports:view')
  const canViewTenantReports = useTenantWidePermission('reports:view')
  const canViewEscalations = usePermission('escalations:view')
  const canViewSegments = usePermission('segments:view')
  const canViewSessions = usePermission('sessions:view')
  const canViewBroadcasts = usePermission('broadcasts:view')

  const entityIcon = verticalModule?.entityIcon ?? BuildingOfficeIcon
  const entityLabelPlural = pluralize(labels.entity)
  const groupLabelPlural = pluralize(labels.group)

  // A plan without collections (a CBC-only school) has no money screens: no
  // payments, arrears, payer conversations or collection reports.
  const collects = features[COLLECTIONS] !== false

  const navigation = useMemo<NavItem[]>(() => {
    // Whatever the composed vertical declares, filtered by the tenant's
    // feature switches and the operator's permissions. This file never learns
    // what 'academics' or 'leases' mean -- it only checks the strings.
    const byModel = (operatingModel && verticalModule?.operatingModelNav?.[operatingModel]) || {}
    const visibleModuleItems = (verticalModule?.navItems ?? [])
      // An agency-only page is not on a landlord's menu (and vice versa).
      .filter((item) => !item.operatingModels || !operatingModel || item.operatingModels.includes(operatingModel))
      .filter((item) => !item.requiresFeature || features[item.requiresFeature] !== false)
      .filter(
        (item) =>
          !item.requiresPermission ||
          // A module declares permissions as plain strings: Tier 1 must not
          // require the vertical to import the Permission union to say so.
          (permissions as string[]).includes(item.requiresPermission),
      )
    const toChild = (item: VerticalNavItem): NavChild => ({
      name: byModel.labels?.[item.to] ?? item.label,
      href: item.to,
      icon: item.icon ?? entityIcon,
      keywords: item.keywords,
    })
    const fromModule = (group: VerticalNavItem['group']): NavChild[] =>
      visibleModuleItems.filter((item) => item.group === group).map(toChild)

    // Shared entries use plain, task-first words by default; a vertical may
    // rename them to the nouns its own operators use.
    const renames = { ...(verticalModule?.sharedNavLabels ?? {}), ...(byModel.labels ?? {}) }
    const label = (href: string, fallback: string) => renames[href] ?? fallback
    const hidden = new Set([...(verticalModule?.hiddenSharedNav ?? []), ...(byModel.hidden ?? [])])
    const shown = (pages: NavChild[]) => pages.filter((page) => !hidden.has(page.href))
    const top = (entry: NavChild | null) => {
      if (entry) items.push({ name: entry.name, href: entry.href, icon: entry.icon, keywords: entry.keywords, tabs: entry.tabs })
    }

    const items: NavItem[] = [
      { name: 'Home', href: '/', icon: HomeIcon, keywords: ['home', 'overview', 'dashboard'] },
    ]
    const section = (name: string, children: NavChild[]) => {
      const shown = children.filter((child) => !hidden.has(child.href))
      if (shown.length > 0) items.push({ name, children: shown })
    }

    // 1. The vertical's own day-to-day work comes first: it is why the
    //    operator opened the app. One nav section per declared `section`.
    const workspaceSections: string[] = []
    for (const item of visibleModuleItems) {
      if (item.group === 'workspace' && item.section && !workspaceSections.includes(item.section)) {
        workspaceSections.push(item.section)
      }
    }
    for (const name of workspaceSections) {
      section(
        name,
        visibleModuleItems
          .filter((item) => item.group === 'workspace' && item.section === name)
          .map(toChild),
      )
    }

    // 2. People
    const people: NavChild[] = []
    if (canViewBeneficiaries) {
      people.push({
        name: label('/entities', entityLabelPlural),
        href: '/entities',
        icon: entityIcon,
        keywords: ['students', 'learners', 'units', 'beneficiaries'],
      })
      people.push({
        name: label('/clients', groupLabelPlural),
        href: '/clients',
        icon: UsersIcon,
        keywords: ['parents', 'guardians', 'families', 'tenants', 'payers'],
      })
    }
    people.push(...fromModule('people'))
    section('People', people)

    // 3. Money: incoming payments as one entry, what's owed and agreed as
    //    another, then the vertical's own (e.g. landlord payouts).
    const payments: NavChild[] = []
    if (canViewTenantPayments) {
      payments.push({ name: 'Approvals', href: '/approvals', icon: ClipboardDocumentCheckIcon, keywords: ['approve', 'cash', 'cheque', 'bursary', 'record payment', 'bursar'] })
    }
    if (canViewPayments) {
      payments.push({ name: 'Payments received', href: '/payments-received', icon: BanknotesIcon, keywords: ['transactions', 'ledger', 'mpesa code', 'bank reference', 'incoming'] })
    }
    if (canViewTenantPayments) {
      payments.push({ name: 'Deposits to assign', href: '/deposits-to-assign', icon: InboxArrowDownIcon, keywords: ['unassigned', 'unknown account', 'suspense', 'no owner', 'banked cash'] })
    }
    const arrears: NavChild[] = []
    if (canViewPlans) {
      arrears.push({ name: label('/payment-plans', 'Payment plans'), href: '/payment-plans', icon: CalendarDaysIcon, keywords: ['installments', 'schedules'] })
    }
    if (canViewPledges) {
      arrears.push({ name: label('/pledges', 'Promises to pay'), href: '/pledges', icon: HandThumbUpIcon, keywords: ['pledges', 'commitments', 'promises'] })
    }
    if (canSeeWaivers) {
      arrears.push({ name: 'Waivers', href: '/waivers', icon: ReceiptPercentIcon, keywords: ['waive', 'forgive', 'bursary', 'in kind', 'concession', 'write off', 'repair'] })
    }
    if (collects) {
      section('Money', [
        hub('Payments', BanknotesIcon, shown(payments)),
        // Named for what it holds: ways to settle arrears, not a list of who
        // owes (that is the "Owing" filter and Reports). Search still finds it
        // by "arrears".
        withKeyword(hub('Plans & waivers', CalendarDaysIcon, shown(arrears)), 'arrears'),
        ...fromModule('finance'),
      ].filter((entry): entry is NavChild => entry !== null))
    }

    // 4-6. Messages, Reports and Settings: one entry each, their pages as tabs.
    const messages: NavChild[] = []
    if (canViewEscalations && collects) {
      messages.push({ name: 'Needs follow-up', href: '/escalations', icon: ShieldExclamationIcon, keywords: ['escalations', 'follow up', 'risk', 'alerts'] })
    }
    if (canViewSessions && collects) {
      messages.push({ name: 'Conversations', href: '/conversations', icon: ChatBubbleLeftRightIcon, keywords: ['sessions', 'chat', 'whatsapp', 'messages', 'inbox'] })
    }
    messages.push(...fromModule('engagement'))
    if (canViewBroadcasts) {
      messages.push({ name: 'Bulk messages', href: '/broadcasts', icon: MegaphoneIcon, keywords: ['broadcasts', 'bulk', 'sms', 'notifications'] })
      messages.push({ name: 'Message templates', href: '/templates', icon: DocumentTextIcon, keywords: ['templates'] })
    }
    top(hub('Messages', ChatBubbleLeftRightIcon, shown(messages)))

    const reports: NavChild[] = []
    if (canViewReports && collects) {
      reports.push({ name: 'Reports', href: '/reports', icon: ChartBarSquareIcon, keywords: ['analytics', 'collection', 'aging', 'export'] })
    }
    reports.push(...fromModule('reports'))
    if (canViewTenantReports && collects) {
      reports.push({ name: 'Trends', href: '/insights', icon: LightBulbIcon, keywords: ['insights', 'trends', 'analysis', 'forecast'] })
    }
    if (canViewSegments && collects) {
      reports.push({ name: 'Payer groups', href: '/segments', icon: UserGroupIcon, keywords: ['segments', 'grouping', 'cohorts'] })
    }
    top(hub('Reports', ChartBarSquareIcon, shown(reports)))

    // The organisation's own account, kept out of daily work.
    const settings: NavChild[] = []
    if (canViewSettings) {
      // Each section of Settings is a tab of its own (2026-10-16).
      settings.push(...settingsTabs.map((tab) => ({ name: tab.label, href: tab.href, icon: Cog6ToothIcon,
        keywords: tab.id === 'general' ? ['preferences', 'configuration', 'organisation'] : [tab.label.toLowerCase()] })))
    }
    if (canViewSettings) {
      settings.push({ name: 'Plan & billing', href: '/subscriptions', icon: CreditCardIcon, keywords: ['subscription', 'plan', 'renewal', 'invoice', 'billing'] })
    }
    // Starting balances are entered once, when moving to LipaFlow: setup,
    // not daily money (D6). Their approval is still in Approvals.
    if (canViewTenantPayments && collects) {
      settings.push({ name: 'Switching to LipaFlow', href: '/opening-balances', icon: ScaleIcon, keywords: ['opening balances', 'balance brought forward', 'switchover', 'arrears', 'starting balances'] })
    }
    settings.push(...fromModule('settings'))
    if (canViewAudit) {
      settings.push({ name: 'Activity log', href: '/audit-trail', icon: EyeIcon, keywords: ['audit', 'logs', 'history', 'activity'] })
    }
    top(hub('Settings', Cog6ToothIcon, shown(settings)))

    return soleDestination(items) ? items.filter((item) => item.href !== '/') : items
  }, [settingsTabs, entityLabelPlural, groupLabelPlural, entityIcon, verticalModule, operatingModel, features, collects, permissions, canViewAudit, canViewSettings, canViewBeneficiaries, canViewPayments, canViewTenantPayments, canViewPlans, canViewPledges, canSeeWaivers, canViewReports, canViewTenantReports, canViewEscalations, canViewSegments, canViewSessions, canViewBroadcasts])

  return { navigation, labels, entityLabelPlural, groupLabelPlural, entityIcon }
}
